import { useState } from "react";
import api from "@/lib/apiClient";
import { PageHeader, SectionCard } from "@/components/common";
import { formatINR, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { FileText, Download, TrendingUp, Wallet, Package, Users, Landmark, Megaphone, Loader2, BarChart3 } from "lucide-react";
import { toast } from "sonner";

const REPORTS = [
  { key: "sales", label: "Sales Report", icon: TrendingUp, desc: "Every invoice with totals & payment method" },
  { key: "pnl", label: "Profit & Loss", icon: BarChart3, desc: "Revenue, cost, margins & net profit" },
  { key: "expenses", label: "Expense Report", icon: Wallet, desc: "Spending by category" },
  { key: "inventory", label: "Inventory Report", icon: Package, desc: "Stock levels & valuation" },
  { key: "customers", label: "Customer Report", icon: Users, desc: "CLV, segments & outstanding" },
  { key: "cashflow", label: "Cash Flow", icon: Landmark, desc: "Money in vs out" },
  { key: "marketing", label: "Marketing Report", icon: Megaphone, desc: "Campaign ROI & attribution" },
];

const HIDDEN_COLS = new Set(["id", "business_id", "branch_id", "customer_id", "product_id", "_id", "created_at"]);

function visibleKeys(row) {
  return Object.keys(row).filter((k) => typeof row[k] !== "object" && !HIDDEN_COLS.has(k));
}

function toCSV(rows) {
  if (!rows.length) return "";
  const keys = visibleKeys(rows[0]);
  const head = keys.join(",");
  const body = rows.map((r) => keys.map((k) => `"${String(r[k] ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  return head + "\n" + body;
}

export default function Reports() {
  const [loading, setLoading] = useState(null);
  const [preview, setPreview] = useState(null);

  const generate = async (key, label) => {
    setLoading(key);
    try {
      const r = await api.get(`/reports/${key}`);
      let rows = [];
      if (r.data.rows) rows = r.data.rows;
      else if (r.data.expenses) rows = r.data.expenses;
      else if (r.data.campaigns) rows = r.data.campaigns;
      else if (r.data.recent_transactions) rows = r.data.recent_transactions;
      else if (Array.isArray(r.data)) rows = r.data;
      setPreview({ label, key, rows, raw: r.data });
    } catch { toast.error("Failed to load report"); }
    finally { setLoading(null); }
  };

  const download = () => {
    if (!preview?.rows?.length) return toast.error("Nothing to export");
    const csv = toCSV(preview.rows);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `khevora-${preview.key}-report.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Report downloaded");
  };

  return (
    <div>
      <PageHeader title="Reports" subtitle="Generate and export professional business reports." />

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {REPORTS.map((r, i) => (
          <button key={r.key} data-testid={`report-${r.key}`} onClick={() => generate(r.key, r.label)}
            className="text-left rounded-xl border border-border bg-card p-5 shadow-sm hover:-translate-y-1 hover:border-primary/40 hover:shadow-lg transition-transform duration-200 animate-fade-up" style={{ animationDelay: `${i * 40}ms` }}>
            <div className="flex items-center justify-between">
              <div className="grid place-items-center h-10 w-10 rounded-lg bg-primary/10 text-primary"><r.icon className="h-5 w-5" /></div>
              {loading === r.key ? <Loader2 className="h-4 w-4 animate-spin text-primary" /> : <FileText className="h-4 w-4 text-muted-foreground" />}
            </div>
            <div className="font-heading font-semibold mt-3">{r.label}</div>
            <div className="text-xs text-muted-foreground mt-1">{r.desc}</div>
          </button>
        ))}
      </div>

      {preview && (
        <SectionCard title={preview.label} subtitle={`${preview.rows.length} records`} testid="report-preview"
          action={<Button onClick={download} size="sm" data-testid="download-report-btn" className="font-semibold"><Download className="h-4 w-4 mr-1.5" /> Export CSV</Button>}>
          {preview.rows.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">This report has summary data — {preview.key === "pnl" ? `Net profit ${formatINR(preview.raw?.kpis?.net_profit || 0)}` : "no tabular rows to preview"}.</p>
          ) : (
            <div className="overflow-x-auto max-h-[500px]">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-card"><tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                  {visibleKeys(preview.rows[0]).slice(0, 6).map((k) => <th key={k} className="text-left font-semibold py-3 px-2">{k.replace(/_/g, " ")}</th>)}
                </tr></thead>
                <tbody>
                  {preview.rows.slice(0, 100).map((row, i) => (
                    <tr key={i} className="border-b border-border/50">
                      {visibleKeys(preview.rows[0]).slice(0, 6).map((k) => {
                        let v = row[k];
                        if (/price|total|amount|spent|value|payable|purchased|sales|revenue|outstanding|subtotal|tax|discount/.test(k) && typeof v === "number") v = formatINR(v);
                        else if (/date|_at|purchase/.test(k) && typeof v === "string" && v.includes("T")) v = formatDate(v);
                        return <td key={k} className="py-2.5 px-2 truncate max-w-[180px]">{String(v ?? "—")}</td>;
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>
      )}
    </div>
  );
}
